'use client'

import React from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { formatCurrency } from '../utils/currency'
import type { BirhausCurrencyDisplayProps } from '../types/financial'

// Local cn utility
const cn = (...inputs: any[]) => twMerge(clsx(inputs))

/**
 * BirhausCurrencyDisplay - Currency amount display component
 * 
 * Features:
 * - Paraguay-first currency formatting (Guaraní as default)
 * - SEPRELAD compliant number formatting
 * - Multiple currency support (PYG, USD, EUR, BRL, ARS)
 * - Accessibility with proper ARIA labels
 * - Spanish-first localization
 * - Configurable symbol and code display
 */
export function BirhausCurrencyDisplay({
  amount,
  currency = 'PYG',
  locale,
  showSymbol = true,
  showCode = false,
  className,
  ...props
}: BirhausCurrencyDisplayProps) {
  const { language, reportViolation } = useBirhaus()

  // BIRHAUS validation: Check for potentially problematic amounts
  React.useEffect(() => {
    // SEPRELAD compliance: Flag large amounts for potential reporting
    if (currency === 'USD' && amount >= 10000) {
      reportViolation({
        type: 'performance',
        severity: 'warning',
        element: document.createElement('span'), // Placeholder element
        message: `USD amount ${amount} exceeds SEPRELAD reporting threshold`,
        messageEs: `Monto USD ${amount} supera umbral de reporte SEPRELAD`,
        messageEn: `USD amount ${amount} exceeds SEPRELAD reporting threshold`,
        recommendation: 'Ensure proper SEPRELAD compliance reporting for amounts ≥ $10,000 USD',
        birhausPrinciple: 8 // Compliance principle (hypothetical extension)
      })
    }

    // Validate reasonable amount ranges
    if (Math.abs(amount) > 1000000000) { // 1 billion limit
      reportViolation({
        type: 'performance',
        severity: 'warning',
        element: document.createElement('span'),
        message: `Currency amount ${amount} exceeds reasonable display limits`,
        messageEs: `Monto ${amount} supera límites razonables de visualización`,
        messageEn: `Currency amount ${amount} exceeds reasonable display limits`,
        recommendation: 'Consider using abbreviated notation for very large amounts',
        birhausPrinciple: 1 // Cognitive load reduction
      })
    }
  }, [amount, currency, reportViolation])

  // Use provided locale or infer from BIRHAUS language
  const displayLocale = locale || (language === 'es' ? 'es-PY' : 'en-US')

  // Format the currency amount
  const formattedAmount = formatCurrency(amount, {
    currency,
    locale: displayLocale,
    showSymbol,
    showCode
  })

  // Determine text color based on amount (common financial UX pattern)
  const amountColorClass = cn(
    'font-mono', // Monospace for better number alignment
    {
      'text-green-600': amount > 0,
      'text-red-600': amount < 0,
      'text-birhaus-muted-foreground': amount === 0
    }
  )

  // ARIA label for accessibility
  const ariaLabel = React.useMemo(() => {
    const absAmount = Math.abs(amount)
    const sign = amount < 0 ? (language === 'es' ? 'menos ' : 'negative ') : ''
    
    // Currency names in Spanish and English
    const currencyNames = {
      PYG: { es: 'guaraníes', en: 'Paraguayan guaraníes' },
      USD: { es: 'dólares americanos', en: 'US dollars' },
      EUR: { es: 'euros', en: 'euros' },
      BRL: { es: 'reales brasileños', en: 'Brazilian reais' },
      ARS: { es: 'pesos argentinos', en: 'Argentine pesos' }
    }

    const currencyName = currencyNames[currency]?.[language] || currencyNames[currency]?.es
    
    return `${sign}${absAmount.toLocaleString(displayLocale)} ${currencyName}`
  }, [amount, currency, language, displayLocale])

  return (
    <span
      className={cn(
        'inline-flex items-center',
        amountColorClass,
        className
      )}
      aria-label={ariaLabel}
      title={ariaLabel}
      {...props}
    >
      {formattedAmount}
    </span>
  )
}

export type { BirhausCurrencyDisplayProps }